import { useEffect } from 'react';

// Default Medentic favicon URL
const DEFAULT_FAVICON_URL = "https://assets.medentic.app/logos/pwa/favicon-32x32.png";

/**
 * Lightweight hook to update page title and favicon
 * @param title - Page title to set
 * @param imageUrl - Optional image URL for favicon (falls back to default if not provided)
 */
export const usePageMeta = (title?: string, imageUrl?: string) => {
  useEffect(() => {
    if (title) {
      document.title = title;
    }
  }, [title]);

  useEffect(() => {
    const updateFavicon = (url: string) => {
      // Remove existing favicon links
      const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
      existingFavicons.forEach((favicon) => favicon.remove());

      // Use provided URL or fallback to default
      let faviconUrl = url && url.trim() !== "" ? url : DEFAULT_FAVICON_URL;
      
      // Convert relative URLs to absolute URLs if needed
      if (faviconUrl !== DEFAULT_FAVICON_URL && !faviconUrl.startsWith("http")) {
        if (faviconUrl.startsWith("/")) {
          faviconUrl = window.location.origin + faviconUrl;
        } else {
          faviconUrl = window.location.origin + "/" + faviconUrl;
        }
      }

      // Create new favicon link
      const link = document.createElement("link");
      link.rel = "icon";
      link.type = "image/x-icon";
      link.href = faviconUrl;
      
      // Add to document head
      document.head.appendChild(link);

      // Also update apple-touch-icon for better mobile support
      const appleTouchIcon = document.createElement("link");
      appleTouchIcon.rel = "apple-touch-icon";
      appleTouchIcon.href = faviconUrl;
      document.head.appendChild(appleTouchIcon);

      // Add error handling for favicon loading
      link.onerror = () => {
        // If custom favicon fails to load, fallback to Medentic favicon
        if (faviconUrl !== DEFAULT_FAVICON_URL) {
          link.href = DEFAULT_FAVICON_URL;
          appleTouchIcon.href = DEFAULT_FAVICON_URL;
        }
      };
    };

    // Always update favicon (will use default if imageUrl is not provided)
    updateFavicon(imageUrl || DEFAULT_FAVICON_URL);
  }, [imageUrl]);
};

export default usePageMeta;

